import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useRouteMatch } from 'react-router-dom';
import cx from 'classnames';
import { ChevronDown, Settings } from 'lucide-react';
import {
    SidebarDivider,
    SidebarIcon,
    sidebarItemClassName,
} from 'src/components/Buttons/NavItem';
import { isOrganizationAdministrator } from 'src/domain/user';
import { useSignedInUser } from 'src/view/hooks/useSignedInUser';
import {
    useActiveOrgSection,
    OrgSection,
} from 'src/view/hooks/useActiveOrgSection';

const SECTIONS: { id: OrgSection; labelKey: string }[] = [
    { id: 'org-settings', labelKey: 'modules.organization.settings' },
    {
        id: 'org-hierarchy',
        labelKey: 'admin.forms.createOrganization.hierarchyTitle',
    },
    {
        id: 'org-groups',
        labelKey: 'admin.forms.createOrganization.groupsTitle',
    },
    {
        id: 'org-roles',
        labelKey: 'admin.forms.createOrganization.rolesTitle',
    },
];

export const LinkToOrganization = () => {
    const user = useSignedInUser();
    const { t } = useTranslation();
    const history = useHistory();
    const isOnOrgPage = Boolean(useRouteMatch('/app/organization'));
    const { activeSection, scrollToSection } = useActiveOrgSection();
    const [expanded, setExpanded] = useState(isOnOrgPage);

    useEffect(() => {
        if (isOnOrgPage) {
            setExpanded(true);
        }
    }, [isOnOrgPage]);

    if (!isOrganizationAdministrator(user)) return null;

    const handleSectionClick = (sectionId: string) => {
        if (isOnOrgPage) {
            scrollToSection(sectionId);
        } else {
            history.push(`/app/organization#${sectionId}`);
        }
    };

    const handleOrganizationClick = () => {
        if (isOnOrgPage) {
            setExpanded(current => !current);
            return;
        }

        setExpanded(true);
        history.push('/app/organization');
    };

    const showSections = isOnOrgPage && expanded;

    return (
        <>
            <SidebarDivider />
            <button
                type="button"
                aria-expanded={showSections}
                aria-controls="organization-navigation-items"
                onClick={handleOrganizationClick}
                className={sidebarItemClassName(false)}
            >
                <SidebarIcon icon={Settings} />
                <p className="tracking-tight font-body align-middle text-left flex-1 whitespace-nowrap">
                    {t('modules.organization.navLabel')}
                </p>
                <SidebarIcon
                    icon={ChevronDown}
                    className={cx(
                        'transition-transform duration-200',
                        showSections ? 'rotate-180' : 'rotate-0',
                    )}
                />
            </button>
            <ul
                id="organization-navigation-items"
                className={cx(
                    // ml-7 puts the guide line under the center of the gear icon
                    'flex flex-col gap-1 w-auto self-stretch ml-7 pl-1.5 border-l border-starkWhite transition-all duration-300',
                    showSections
                        ? 'max-h-96 opacity-100 my-1'
                        : 'max-h-0 opacity-0 overflow-hidden',
                )}
            >
                {SECTIONS.map(section => (
                    <li key={section.id}>
                        <button
                            onClick={() => handleSectionClick(section.id)}
                            className={cx(
                                'pl-2.5 text-sm text-left tracking-tight',
                                sidebarItemClassName(
                                    activeSection === section.id,
                                ),
                            )}
                        >
                            {t(section.labelKey)}
                        </button>
                    </li>
                ))}
            </ul>
        </>
    );
};
